/**
 * BrushingTechniqueCard — техника на четкане с анимация, квадрантен таймер
 * и проверка след четкане.
 * Данни: GET /api/dental-prevention/brushing · POST /brushing/session
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brush, Play, Square, Volume2, VolumeX, Vibrate, CheckCircle2, Circle,
  Loader2, Flame,
} from "lucide-react";
import { useAuth } from "@/App";

const PREF_KEY = "dhi:brush:cue";

function BrushAnimation({ technique, active, isDark }) {
  const sweep = technique === "modified_bass";
  return (
    <div className="relative rounded-xl overflow-hidden"
      style={{ background: isDark ? "rgba(255,255,255,.04)" : "#F1F5F9" }}
      data-testid="brush-animation">
      <style>{`
        @keyframes dhiBrushVib { 0%,100%{transform:translate(0,0)} 25%{transform:translate(1.6px,-1px)} 75%{transform:translate(-1.6px,1px)} }
        @keyframes dhiBrushSweep { 0%,55%{transform:translate(0,0)} 80%{transform:translate(2px,-16px)} 100%{transform:translate(0,0)} }
        .dhi-brush-run { animation: dhiBrushVib .14s linear infinite; }
        .dhi-brush-sweep { animation: dhiBrushSweep 2.4s ease-in-out infinite; }
      `}</style>
      <svg viewBox="0 0 240 140" className="w-full h-36">
        {/* зъб */}
        <path d="M70 30 q30 -14 60 0 q10 34 -6 62 q-10 18 -24 18 q-14 0 -24 -18 q-16 -28 -6 -62 z"
          fill={isDark ? "#E2E8F0" : "#FFFFFF"} stroke="#94A3B8" strokeWidth="2" />
        {/* венец */}
        <path d="M46 96 q54 -22 108 0 q6 26 -54 26 q-60 0 -54 -26 z"
          fill="#F472A0" opacity="0.85" />
        {/* жлеб */}
        <path d="M74 96 q46 -16 92 0" fill="none" stroke="#BE185D"
          strokeWidth="1.5" strokeDasharray="3 3" opacity=".8" />
        {/* четка на 45° */}
        <g transform="translate(150 78) rotate(45)"
          className={active ? (sweep ? "dhi-brush-sweep" : "dhi-brush-run") : ""}>
          <rect x="-4" y="-6" width="58" height="12" rx="6" fill="#0EA5E9" />
          <g stroke="#CBD5E1" strokeWidth="2.5">
            {[0, 6, 12, 18, 24].map((x) => (
              <line key={x} x1={-2 + x} y1="-6" x2={-2 + x} y2="-16" />
            ))}
          </g>
        </g>
        <text x="12" y="26" fontSize="10" fill={isDark ? "#94A3B8" : "#475569"}>
          45° към венеца{sweep ? " + помитане навън" : ""}
        </text>
      </svg>
    </div>
  );
}

export default function BrushingTechniqueCard({ isDark = false, cardCls = "", childId = null }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [tech, setTech] = useState("modified_bass");
  const [cue, setCue] = useState(() => localStorage.getItem(PREF_KEY) || "sound_vibe");
  const [qi, setQi] = useState(-1);
  const [left, setLeft] = useState(0);
  const [checks, setChecks] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const audio = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-prevention/brushing`, {
        params: childId ? { child_id: childId } : {},
      });
      setD(data);
      if (data.last?.technique) setTech(data.last.technique);
    } catch { setD(null); }
  }, [API, childId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(timer.current), []);

  const beep = () => {
    if (cue === "vibe") { navigator.vibrate?.(180); return; }
    if (cue === "silent") return;
    navigator.vibrate?.(120);
    try {
      audio.current = audio.current || new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audio.current;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 660; o.type = "sine";
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.36);
    } catch { /* без звук */ }
  };

  if (!d) return null;
  const border = isDark ? "border-white/10" : "border-gray-200";
  const t = d.techniques.find((x) => x.key === tech) || d.techniques[0];
  const running = qi >= 0;

  const start = () => {
    clearInterval(timer.current);
    let idx = 0, rem = d.quadrants[0].seconds;
    setQi(0); setLeft(rem); beep();
    timer.current = setInterval(() => {
      rem -= 1;
      if (rem <= 0) {
        idx += 1;
        if (idx >= d.quadrants.length) {
          clearInterval(timer.current);
          setQi(-2); setLeft(0); beep();
          return;
        }
        rem = d.quadrants[idx].seconds;
        setQi(idx); beep();
      }
      setLeft(rem);
    }, 1000);
  };

  const stop = () => { clearInterval(timer.current); setQi(-1); setLeft(0); };

  const finish = async () => {
    setBusy(true);
    try {
      const total = d.quadrants.reduce((s, q) => s + q.seconds, 0);
      const { data } = await axios.post(`${API}/dental-prevention/brushing/session`, {
        technique: tech, duration_sec: total, post_check: checks,
        slot: new Date().getHours() >= 15 ? "pm" : "am",
        child_id: childId,
      });
      setD(data); setChecks([]); setQi(-1);
      toast.success(data.result?.failed?.length
        ? "Записано — вижте къде остава плака"
        : "Перфектно четкане — отметнато в дневника");
    } catch { toast.error("Сесията не се записа"); }
    finally { setBusy(false); }
  };

  const cueIcon = cue === "silent" ? VolumeX : cue === "vibe" ? Vibrate : Volume2;
  const CueIcon = cueIcon;
  const nextCue = () => {
    const order = ["sound_vibe", "vibe", "silent"];
    const v = order[(order.indexOf(cue) + 1) % order.length];
    setCue(v); localStorage.setItem(PREF_KEY, v);
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-brushing-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Brush className="w-4 h-4 text-cyan-500" /> Техника на четкане
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {d.sessions_7d > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-600 inline-flex items-center gap-1"
              data-testid="brushing-count">
              <Flame className="w-3 h-3" /> {d.sessions_7d} за 7 дни
            </span>
          )}
          {d.clean_pct !== null && d.clean_pct !== undefined && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-600"
              data-testid="brushing-clean">
              {d.clean_pct}% чисти
            </span>
          )}
          <button onClick={nextCue} title="Звук и вибрация"
            className={`p-1.5 rounded-lg border ${border}`} data-testid="brushing-cue-toggle">
            <CueIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* избор на техника */}
      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="brushing-techniques">
        {d.techniques.map((x) => (
          <button key={x.key} onClick={() => setTech(x.key)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition ${
              tech === x.key ? "border-cyan-500 bg-cyan-500/15 text-cyan-500" : `${border} opacity-70`}`}
            data-testid={`brushing-tech-${x.key}`}>
            {x.title_bg}
          </button>
        ))}
      </div>

      <div className="mt-3 grid md:grid-cols-2 gap-3">
        <div>
          <BrushAnimation technique={tech} active={running} isDark={isDark} />
          <div className="text-[10px] opacity-70 mt-1.5">{t.for_whom_bg}</div>

          {/* таймер */}
          <div className={`mt-2 p-3 rounded-xl border ${border}`} data-testid="brushing-timer">
            {qi >= 0 ? (
              <div>
                <div className="text-[11px] font-bold">
                  {d.quadrants[qi].label_bg}
                </div>
                <div className="text-3xl font-extrabold tabular-nums" data-testid="brushing-seconds">
                  {left}s
                </div>
                <div className="flex gap-1 mt-1.5">
                  {d.quadrants.map((q, i) => (
                    <div key={q.key} className="flex-1 h-1.5 rounded-full"
                      style={{ background: i < qi ? "#10B981" : i === qi ? "#0EA5E9"
                        : (isDark ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.08)") }} />
                  ))}
                </div>
              </div>
            ) : qi === -2 ? (
              <div className="text-[11px] font-bold text-emerald-500">
                Две минути са готови — направете проверката отдолу.
              </div>
            ) : (
              <div className="text-[11px] opacity-70">
                Четири квадранта по 30 секунди. Не местете четката бързо — тя
                вибрира на място.
              </div>
            )}
            <div className="flex gap-2 mt-2">
              {qi >= 0 ? (
                <button onClick={stop}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border ${border} inline-flex items-center gap-1`}
                  data-testid="brushing-stop">
                  <Square className="w-3.5 h-3.5" /> Спри
                </button>
              ) : (
                <button onClick={start}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1"
                  data-testid="brushing-start">
                  <Play className="w-3.5 h-3.5" /> Пусни таймера
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">
            Стъпки
          </div>
          <ol className="space-y-1">
            {t.steps_bg.map((s, i) => (
              <li key={i} className="text-[11px] opacity-85">{i + 1}. {s}</li>
            ))}
          </ol>
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mt-2 mb-1">
            Чести грешки
          </div>
          <ul className="space-y-0.5">
            {t.errors_bg.map((s, i) => (
              <li key={i} className="text-[10px] text-rose-500/90">· {s}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* проверка след четкане */}
      <div className="mt-3" data-testid="brushing-postcheck">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">
          Проверка след четкане
        </div>
        <div className="space-y-1">
          {d.post_check.map((c) => (
            <button key={c.key}
              onClick={() => setChecks(checks.includes(c.key)
                ? checks.filter((k) => k !== c.key) : [...checks, c.key])}
              className={`w-full text-left p-2 rounded-lg border ${border} flex items-start gap-2`}
              data-testid={`brushing-check-${c.key}`}>
              {checks.includes(c.key)
                ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                : <Circle className="w-4 h-4 opacity-50 shrink-0 mt-0.5" />}
              <span className="min-w-0">
                <span className="text-[11px] font-bold block">{c.label_bg}</span>
                {!checks.includes(c.key) && (
                  <span className="text-[10px] opacity-65">{c.fail_bg}</span>
                )}
              </span>
            </button>
          ))}
        </div>
        <button onClick={finish} disabled={busy}
          className="mt-2 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1 disabled:opacity-60"
          data-testid="brushing-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Запиши четкането
        </button>
      </div>

      {d.weak_points_bg?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="brushing-weak">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50">
            Къде Ви убягва най-често
          </div>
          {d.weak_points_bg.map((w, i) => (
            <div key={i} className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="text-[11px] font-bold">{w.label_bg} · {w.times} пъти</div>
              <div className="text-[10px] opacity-80">{w.fail_bg}</div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
